// Authors: Delaney Gillilan
// Icon: material-symbols:cloud-download
// Slug: Merge fragments into the DOM using a Server-Sent Event
// Description: Remember, SSE is just a regular SSE request but with the ability to send 0-inf messages to the client.

import {
  DefaultFragmentMergeMode,
  EventTypeMergeFragments,
  FragmentMergeModeAfter,
  FragmentMergeModeAppend,
  FragmentMergeModeBefore,
  FragmentMergeModeInner,
  FragmentMergeModeMorph,
  FragmentMergeModeOuter,
  FragmentMergeModePrepend,
  FragmentMergeModeUpsertAttributes,
} from '../../../../engine/consts'
import { initErr } from '../../../../engine/errors'
import {
  type HTMLOrSVGElement,
  type InitContext,
  PluginType,
  type WatcherPlugin,
} from '../../../../engine/types'
import { walkDOM } from '../../../../utils/dom'
import { isBoolString } from '../../../../utils/text'
import {
  docWithViewTransitionAPI,
  supportsViewTransitions,
} from '../../../../utils/view-transtions'
import { datastarSSEEventWatcher } from '../shared'

const DIV = 'div'
const VALUE = 'value'
const createEl = (tagName: string) => document.createElement(tagName)
const createDiv = () => createEl(DIV) as HTMLDivElement
const tagRaw = (tagName: string) => `<${tagName}></${tagName}>`
const elIDs = (el: Element) => [...el.querySelectorAll('[id]')]
const isElement = (el: Node) => el instanceof Element
const isTemplate = (el: Node) => el instanceof HTMLTemplateElement
const isInput = (el: Node) => el instanceof HTMLInputElement
const isTextArea = (el: Node) => el instanceof HTMLTextAreaElement
const isOption = (el: Node) => el instanceof HTMLOptionElement

export const MergeFragments: WatcherPlugin = {
  type: PluginType.Watcher,
  name: EventTypeMergeFragments,
  onGlobalInit: async (ctx) => {
    const fragmentContainer = createEl('template') as HTMLTemplateElement
    datastarSSEEventWatcher(
      EventTypeMergeFragments,
      ({
        fragments: fragmentsRaw = tagRaw(DIV),
        selector = '',
        mergeMode = DefaultFragmentMergeMode,
        useViewTransition: useViewTransitionRaw = 'false',
      }) => {
        const useViewTransition = isBoolString(useViewTransitionRaw)

        fragmentContainer.innerHTML = fragmentsRaw.trim()
        for (const fragment of [...fragmentContainer.content.children]) {
          if (!isElement(fragment)) {
            throw initErr('NoFragmentsFound', ctx)
          }

          const selectorOrID = selector || `#${fragment.getAttribute('id')}`
          const targets = document.querySelectorAll(selectorOrID)
          if (!targets.length) {
            throw initErr('NoTargetsFound', ctx, { selectorOrID })
          }

          if (useViewTransition && supportsViewTransitions) {
            docWithViewTransitionAPI.startViewTransition(() =>
              applyToTargets(
                ctx,
                mergeMode,
                fragment as HTMLOrSVGElement,
                targets,
              ),
            )
          } else {
            applyToTargets(
              ctx,
              mergeMode,
              fragment as HTMLOrSVGElement,
              targets,
            )
          }
        }
      },
    )
  },
}

function applyToTargets(
  ctx: InitContext,
  mergeMode: string,
  fragment: HTMLOrSVGElement,
  capturedTargets: NodeListOf<Element>,
) {
  for (const target of capturedTargets) {
    // Clone the fragment to merge to avoid modifying the original and force browsers to merge the fragment into the DOM
    const fragmentToMerge = fragment.cloneNode(true) as HTMLOrSVGElement

    switch (mergeMode) {
      case FragmentMergeModeMorph: {
        // Morph the target element with the cloned fragment
        morph(target, fragmentToMerge)

        // Clean up and reapply all plugins in the merged fragment (a necessary evil!)
        walkDOM(target, (el) => {
          const cleanups = ctx.removals.get(el)
          if (ctx.removals.delete(el)) {
            for (const cleanup of cleanups!.values()) {
              cleanup()
            }
            cleanups!.clear()
          }
          ctx.applyToElement(el)
        })
        break
      }
      case FragmentMergeModeInner:
        // Replace the contents of the target element with the outer HTML of the response
        target.innerHTML = fragmentToMerge.outerHTML
        break
      case FragmentMergeModeOuter:
        // Replace the entire target element with the response
        target.replaceWith(fragmentToMerge)
        break
      case FragmentMergeModePrepend:
        // Insert the response before the first child of the target element
        target.prepend(fragmentToMerge)
        break
      case FragmentMergeModeAppend:
        // Insert the response after the last child of the target element
        target.append(fragmentToMerge)
        break
      case FragmentMergeModeBefore:
        // Insert the response before the target element
        target.before(fragmentToMerge)
        break
      case FragmentMergeModeAfter:
        // Insert the response after the target element
        target.after(fragmentToMerge)
        break
      case FragmentMergeModeUpsertAttributes:
        // Upsert the attributes of the target element
        for (const attrName of fragmentToMerge.getAttributeNames()) {
          const value = fragmentToMerge.getAttribute(attrName)!
          target.setAttribute(attrName, value)
        }
        break
      default:
        throw initErr('InvalidMergeMode', ctx, { mergeMode })
    }
  }
}

const ctxPantry = createDiv()
ctxPantry.hidden = true

let ctxTarget: Element
const ctxIdMap = new Map<Node, Set<string>>()
const ctxPersistentIds = new Set<string>()

// @ts-ignore
export const moveBeforeSupported = ctxPantry.moveBefore !== undefined

function morph(oldNode: Element, newContent: HTMLOrSVGElement): Node[] {
  const normalizedNode = createDiv() as HTMLOrSVGElement
  normalizedNode.append(newContent as Node)
  const newIdElements = elIDs(normalizedNode)

  // all elements with an ID contained within the root element and its descendants
  const oldIdElements = elIDs(oldNode)
  if (oldNode.id) {
    oldIdElements.push(oldNode)
  }

  // Computes the set of ids that persist between the two contents excluding duplicates
  const duplicateIds = new Set<string>()

  const oldIdTagNameMap = new Map<string, string>()
  for (const { id, tagName } of oldIdElements) {
    if (oldIdTagNameMap.has(id)) {
      duplicateIds.add(id)
    } else {
      oldIdTagNameMap.set(id, tagName)
    }
  }

  ctxPersistentIds.clear()
  for (const { id, tagName } of newIdElements) {
    if (ctxPersistentIds.has(id)) {
      duplicateIds.add(id)
    } else if (oldIdTagNameMap.get(id) === tagName) {
      ctxPersistentIds.add(id)
    }
    // skip if tag types mismatch because its not possible to morph one tag into another
  }

  for (const id of duplicateIds) {
    ctxPersistentIds.delete(id)
  }

  // Computes a map of nodes to all ids contained within that node (inclusive of the node).
  // This map can be used to ask if two nodes have intersecting sets of ids,
  // which allows for a looser definition of "matching" than tradition id matching,
  // and allows child nodes to contribute to a parent nodes matching.
  // const idMap = new Map<Node, Set<string>>()
  ctxIdMap.clear()
  populateIdMapWithTree(oldNode.parentElement, oldIdElements)
  populateIdMapWithTree(normalizedNode, newIdElements)

  document.body.insertAdjacentElement('afterend', ctxPantry)

  ctxTarget = oldNode

  // Morph just the outerHTML of the oldNode to the newContent
  // We have to be careful because the oldNode could have siblings which need to be untouched
  // oldParent can be a document or documentFragment but we can treat these as Element
  const oldParent = oldNode.parentNode as Element

  // store start and end points so we can find inbetween nodes to return later
  // as we can avoid returning before or after siblings
  const beforeStartPoint = oldNode.previousSibling
  const endPoint = oldNode.nextSibling

  morphChildren(
    oldParent,
    normalizedNode,
    // these two optional params are the secret sauce
    oldNode, // start point for iteration
    endPoint, // end point for iteration
  )

  const nodes: Node[] = []

  // return array from the first node added to before the last node
  let cursor = beforeStartPoint?.nextSibling || oldParent.firstChild
  while (cursor && cursor !== endPoint) {
    nodes.push(cursor)
    cursor = cursor.nextSibling
  }
  ctxPantry.remove()
  return nodes
}

// This is the core algorithm for matching up children.
// The idea is to use id sets to try to match up nodes as faithfully as possible.
// We greedily match, which allows us to keep the algorithm fast,
// but by using id sets, we are able to better match up with content deeper in the DOM.
function morphChildren(
  oldParent: Element, // the old content that we are merging the new content into
  newParent: Element, // the parent element of the new content
  insertionPoint: Node | null = null, // // the point in the DOM we start morphing at (defaults to first child)
  endPoint: Node | null = null, // the point in the DOM we stop morphing at (defaults to after last child)
) {
  // normalize
  if (isTemplate(oldParent) && isTemplate(newParent)) {
    // we can pretend the DocumentFragment is an Element
    oldParent = oldParent.content as unknown as Element
    newParent = newParent.content as unknown as Element
  }
  insertionPoint ??= oldParent.firstChild

  // run through all the new content
  for (const newChild of newParent.childNodes) {
    // once we reach the end of the old parent content skip to the end and insert the rest
    if (insertionPoint && insertionPoint !== endPoint) {
      const bestMatch = findBestMatch(newChild, insertionPoint, endPoint)
      if (bestMatch) {
        // if the node to morph is not at the insertion point then remove/move up to it
        if (bestMatch !== insertionPoint) {
          let cursor: Node | null = insertionPoint
          // Remove nodes between the start and end nodes
          while (cursor && cursor !== bestMatch) {
            const tempNode = cursor
            cursor = cursor.nextSibling
            removeNode(tempNode)
          }
        }
        morphNode(bestMatch, newChild)
        insertionPoint = bestMatch.nextSibling
        continue
      }
    }

    // if the matching node is elsewhere in the original content
    if (isElement(newChild) && ctxPersistentIds.has(newChild.id)) {
      // move it and all its children here and morph, will always be found
      // Search for an element by id within the document and pantry, and move it using moveBefore.
      const ncID = newChild.id
      const qsTemplate = `[id="${ncID}"]`
      const qs = (el: Element) => el.querySelector(qsTemplate)
      const movedChild = ((ctxTarget.id === ncID && ctxTarget) ||
        qs(ctxTarget) ||
        qs(ctxPantry))!

      // Removes an element from its ancestors' id maps.
      // This is needed when an element is moved from the "future" via `moveBeforeId`.
      // Otherwise, its erstwhile ancestors could be mistakenly moved to the pantry rather than being deleted,
      // preventing their removal hooks from being called.
      let current = movedChild
      while ((current = current.parentNode as Element)) {
        const idSet = ctxIdMap.get(current)
        if (idSet) {
          idSet.delete(movedChild.id)
          if (!idSet.size) {
            ctxIdMap.delete(current)
          }
        }
      }

      moveBefore(oldParent, movedChild, insertionPoint)
      morphNode(movedChild, newChild)
      insertionPoint = movedChild.nextSibling
      continue
    }

    // This performs the action of inserting a new node while handling situations where the node contains
    // elements with persistent ids and possible state info we can still preserve by moving in and then morphing
    if (ctxIdMap.has(newChild)) {
      // node has children with ids with possible state so create a dummy elt of same type and apply full morph algorithm
      const newEmptyChild = createEl((newChild as Element).tagName)
      oldParent.insertBefore(newEmptyChild, insertionPoint)
      morphNode(newEmptyChild, newChild)
      return newEmptyChild
    }
    // optimisation: no id state to preserve so we can just insert a clone of the newChild and its descendants
    const insertedNode = document.importNode(newChild, true) // importNode to not mutate newParent
    oldParent.insertBefore(insertedNode, insertionPoint)
  }

  // remove any remaining old nodes that didn't match up with new content
  while (insertionPoint && insertionPoint !== endPoint) {
    const tempNode = insertionPoint
    insertionPoint = insertionPoint.nextSibling
    removeNode(tempNode)
  }
}

// Scans forward from the startPoint to the endPoint looking for a match for the node.
// It looks for an id set match first, then a soft match.
// We abort softmatching if we find two future soft matches, to reduce churn.
function findBestMatch(
  node: Node,
  startPoint: Node | null,
  endPoint: Node | null,
): Node | null {
  let bestMatch = null
  let nextSibling = node.nextSibling
  let siblingSoftMatchCount = 0
  let displaceMatchCount = 0

  // max id matches we are willing to displace in our search
  const nodeMatchCount = ctxIdMap.get(node)?.size || 0

  let cursor = startPoint
  while (cursor && cursor !== endPoint) {
    // soft matching is a prerequisite for id set matching
    if (isSoftMatch(cursor, node)) {
      let isIdSetMatch = false
      const oldSet = ctxIdMap.get(cursor)
      const newSet = ctxIdMap.get(node)

      if (newSet && oldSet) {
        for (const id of oldSet) {
          // a potential match is an id in the new and old nodes that
          // has not already been merged into the DOM
          // But the newNode content we call this on has not been
          // merged yet and we don't allow duplicate IDs so it is simple
          if (newSet.has(id)) {
            isIdSetMatch = true
            break
          }
        }
      }

      if (isIdSetMatch) {
        return cursor // found an id set match, we're done!
      }

      // we haven't yet saved a soft match fallback
      // the current soft match will hard match something else in the future, leave it
      if (!bestMatch && !ctxIdMap.has(cursor)) {
        // optimization: if node can't id set match, we can just return the soft match immediately
        if (!nodeMatchCount) {
          return cursor
        }
        // save this as the fallback if we get through the loop without finding a hard match
        bestMatch = cursor
      }
    }
    // check for ids we may be displaced when matching
    displaceMatchCount += ctxIdMap.get(cursor)?.size || 0
    if (displaceMatchCount > nodeMatchCount) {
      // if we are going to displace more ids than the node contains then
      // we do not have a good candidate for an id match, so return
      break
    }

    if (bestMatch === null && nextSibling && isSoftMatch(cursor, nextSibling)) {
      // The next new node has a soft match with this node, so
      // increment the count of future soft matches
      siblingSoftMatchCount++
      nextSibling = nextSibling.nextSibling

      // If there are two future soft matches, block soft matching for this node to allow
      // future siblings to soft match. This is to reduce churn in the DOM when an element
      // is prepended.
      if (siblingSoftMatchCount >= 2) {
        bestMatch = undefined
      }
    }

    // if the current node contains active element, stop looking for better future matches,
    // because if one is found, this node will be moved to the pantry, reparenting it and thus losing focus
    if (cursor.contains(document.activeElement)) break

    cursor = cursor.nextSibling
  }

  return bestMatch || null
}

function isSoftMatch(oldNode: Node, newNode: Node): boolean {
  // ok to cast: if one is not element, `id` and `tagName` will be null and we'll just compare that.
  const oldElt = oldNode as Element
  const newElt = newNode as Element

  return (
    oldElt.nodeType === newElt.nodeType &&
    oldElt.tagName === newElt.tagName &&
    // If oldElt has an `id` with possible state and it doesn't match newElt.id then avoid morphing.
    // We'll still match an anonymous node with an IDed newElt, though, because if it got this far,
    // its not persistent, and new nodes can't have any hidden state.
    (!oldElt.id || oldElt.id === newElt.id)
  )
}

// Gets rid of an unwanted DOM node; strategy depends on nature of its reuse:
// - Persistent nodes will be moved to the pantry for later reuse
// - Other nodes will have their hooks called, and then are removed
function removeNode(node: Node) {
  // are we going to id set match this later?
  if (ctxIdMap.has(node)) {
    // skip callbacks and move to pantry
    moveBefore(ctxPantry, node, null)
  } else {
    // remove for realsies
    node.parentNode?.removeChild(node)
  }
}

// hasMoveBeforeSupport type
type PossibleMoveBeforeSupport = Element & {
  moveBefore?: (element: Node, beforeNode: Node | null) => void
}

// Moves an element before another element within the same parent.
// Uses the proposed `moveBefore` API if available (and working), otherwise falls back to `insertBefore`.
// This is essentialy a forward-compat wrapper.
function moveBefore(
  parentNode: PossibleMoveBeforeSupport, // the parent node containing the after element
  element: Node, // the element to be moved
  after: Node | null = null, // the reference node to insert `element` before. If `null`, `element` is appended as the last child
) {
  if (moveBeforeSupported) {
    parentNode.moveBefore!(element, after)
  } else {
    parentNode.insertBefore(element, after)
  }
}

function morphNode(
  oldNode: Node, // root node to merge content into
  newContent: Node, // new content to merge
) {
  // syncs the oldNode to the newNode, copying over all attributes and
  // inner element state from the newNode to the oldNode
  const type = newContent.nodeType

  const oldElt = oldNode as Element
  const newElt = newContent as Element

  // if is an element type, sync the attributes from the
  // new node into the new node
  if (type === 1 /* element type */) {
    const oldAttributes = oldElt.attributes
    const newAttributes = newElt.attributes
    for (const newAttribute of newAttributes) {
      if (oldElt.getAttribute(newAttribute.name) !== newAttribute.value) {
        oldElt.setAttribute(newAttribute.name, newAttribute.value)
      }
    }
    // iterate backwards to avoid skipping over items when a delete occurs
    for (let i = oldAttributes.length - 1; 0 <= i; i--) {
      const oldAttribute = oldAttributes[i]

      // toAttributes is a live NamedNodeMap, so iteration+mutation is unsafe
      // e.g. custom element attribute callbacks can remove other attributes
      if (!oldAttribute) continue

      if (!newElt.hasAttribute(oldAttribute.name)) {
        oldElt.removeAttribute(oldAttribute.name)
      }
    }

    //  many bothans died to bring us information:
    //  https://github.com/patrick-steele-idem/morphdom/blob/master/src/specialElHandlers.js
    //  https://github.com/choojs/nanomorph/blob/master/lib/morph.jsL113
    if (isInput(oldElt) && isInput(newElt) && newElt.type !== 'file') {
      const newValue = newElt.value
      const oldValue = oldElt.value

      // sync boolean attributes
      syncBooleanAttribute(oldElt, newElt, 'checked')
      syncBooleanAttribute(oldElt, newElt, 'disabled')

      const hasBind = Object.keys(newElt.dataset || {}).some((key) =>
        key.startsWith('bind'),
      )

      if (!hasBind) {
        if (!newElt.hasAttribute(VALUE)) {
          oldElt.value = ''
          oldElt.removeAttribute(VALUE)
        } else if (oldValue !== newValue) {
          oldElt.setAttribute(VALUE, newValue)
          oldElt.value = newValue
        }
      }
    } else if (isOption(oldElt) && isOption(newElt)) {
      syncBooleanAttribute(oldElt, newElt, 'selected')
    } else if (isTextArea(oldElt) && isTextArea(newElt)) {
      const newValue = newElt.value
      const oldValue = oldElt.value

      if (newValue !== oldValue) {
        oldElt.value = newValue
      }
      if (oldElt.firstChild && oldElt.firstChild.nodeValue !== newValue) {
        oldElt.firstChild.nodeValue = newValue
      }
    }
  }

  if (type === 8 /* comment */ || type === 3 /* text */) {
    if (oldNode.nodeValue !== newContent.nodeValue) {
      oldNode.nodeValue = newContent.nodeValue
    }
  }

  if (oldElt.innerHTML !== newElt.innerHTML) {
    // newContent can be an element here because .firstChild will be null
    morphChildren(oldElt, newElt)
  }
  return oldNode
}

function syncBooleanAttribute(
  writeTo: Element,
  readFrom: Element,
  attributeName: string,
) {
  const readFromAny = readFrom as any
  const writeToAny = writeTo as any
  const newLiveValue = readFromAny[attributeName]
  const oldLiveValue = writeToAny[attributeName]
  if (newLiveValue !== oldLiveValue) {
    // update attribute's associated DOM property
    writeToAny[attributeName] = readFromAny[attributeName]
    if (newLiveValue) {
      // https://developer.mozilla.org/en-US/docs/Glossary/Boolean/HTML
      // this is the correct way to set a boolean attribute to "true"
      writeTo.setAttribute(attributeName, '')
    } else {
      writeTo.removeAttribute(attributeName)
    }
  }
}

// A bottom-up algorithm that populates a map of Element -> IdSet.
// The idSet for a given element is the set of all IDs contained within its subtree.
// As an optimzation, we filter these IDs through the given list of persistent IDs,
// because we don't need to bother considering IDed elements that won't be in the new content.
function populateIdMapWithTree(
  root: Element | null,
  elements: Element[] | NodeListOf<Element>,
) {
  for (const elt of elements) {
    if (ctxPersistentIds.has(elt.id)) {
      let current: Element | null = elt
      // walk up the parent hierarchy of that element, adding the id of element to the parent's id set
      while (current && current !== root) {
        let idSet = ctxIdMap.get(current)
        // if the id set doesn't exist, create it and insert it in the map
        if (!idSet) {
          idSet = new Set()
          ctxIdMap.set(current, idSet)
        }
        idSet.add(elt.id)
        current = current.parentElement
      }
    }
  }
}
